import React, { useState } from 'react';
import { COMPANY_VERTICAL, COMPANY_VERTICAL_ENUM, COUNTRY_CODE } from '../../Constants/Constants';
import { fetchApiData } from '../../Utils/ApiCalls';
import { DASHBOARD_ENDPOINT } from '../../urls';
import useFormStatus from '../../Hooks/useFormStatus';
import IncreaseFreeTrial from './IncreaseFreeTrial';
import { Button, Input, Checkbox, Select, Typography } from '@attentive-platform/stem-ui';
import { WelcomeEmailFlag } from '../InviteUsers/Flag';

const RegistrationForm = () => {
  const initialFormData = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    company: '',
    country: 'US',
    companyVertical: ''
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [numberOfDays, setNumberOfDays] = useState('1');

  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  const handleDataChange = (val, field) => {
    setFormData({ ...formData, [field]: val });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const {
      send_welcome_email: sendWelcomeEmail,
      email,
      password,
      firstName: first_name,
      lastName: last_name,
      phoneNumber,
      company,
      country,
      companyVertical: company_vertical
    } = formData;

    // Construct the object to send, omitting any empty phone number
    const baseData = {
      email: email.toLowerCase(),
      password,
      first_name,
      last_name,
      company,
      country,
      company_vertical: company_vertical,
      free_trial_days: numberOfDays.replace(/^0+/, ''),
      is_internal: isInternalUser,
      send_welcome_email: sendWelcomeEmail,
      is_construction_signup: false
    };
    if (phoneNumber !== '') baseData.phone_number = phoneNumber;

    const filteredData = baseData;
    // Make the POST request to the API with the form data
    const response = await fetchApiData({
      endpoint: DASHBOARD_ENDPOINT,
      data: filteredData
    });
    handleApiResult(response);

    if (response.error === null) setFormData(initialFormData);
  };

  return (
    <form onSubmit={handleSubmit}>
      {(errorMessage || successMessage) && (
        <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'}`} role='alert'>
          {errorMessage || successMessage}
        </div>
      )}

      <div className='grid grid-cols-2 gap-4 mb-4'>
        <Input
          label='Email'
          type='email'
          value={formData.email}
          onChange={e => handleDataChange(e.target.value, 'email')}
          required
          placeholder='Enter email address'
          fullWidth
        />
        <Input
          label='Password'
          type='text'
          value={formData.password}
          onChange={e => handleDataChange(e.target.value, 'password')}
          required
          placeholder='Enter password'
          fullWidth
        />
      </div>

      <div className='grid grid-cols-2 gap-4 mb-4'>
        <Input
          label='First name'
          type='text'
          value={formData.firstName}
          onChange={e => handleDataChange(e.target.value, 'firstName')}
          required
          placeholder='Enter first name'
          fullWidth
        />
        <Input
          label='Last name'
          type='text'
          value={formData.lastName}
          onChange={e => handleDataChange(e.target.value, 'lastName')}
          required
          placeholder='Enter last name'
          fullWidth
        />
      </div>

      <div className='grid grid-cols-2 gap-4 mb-4'>
        <Input
          label='Phone number'
          type='tel'
          value={formData.phoneNumber}
          onChange={e => handleDataChange(e.target.value, 'phoneNumber')}
          placeholder='Enter phone number'
          required
          fullWidth
        />
        <Input
          label='Company name'
          type='text'
          value={formData.company}
          onChange={e => handleDataChange(e.target.value, 'company')}
          required
          placeholder='Enter company name'
          fullWidth
        />
      </div>

      <div className='mb-2'>
        <Typography variant='body3'>
          Country
        </Typography>
        <Select
          value={formData.country}
          onChange={value => handleDataChange(value, 'country')}
          required
          fullWidth
          placeholder='Select country'
          items={[
            { label: 'Canada', value: COUNTRY_CODE.CANADA },
            { label: 'United States', value: COUNTRY_CODE.USA },
            { label: 'Australia', value: COUNTRY_CODE.AUSTRALIA }
          ]}
        />
      </div>

      <div className='flex gap-2 mb-2'>
        <Checkbox label='Is Admin' checked disabled />
        <Checkbox
          label='Is Internal User'
          checked={isInternalUser}
          onChange={e => setIsInternalUser(e.target.checked)}
        />
      </div>

      <div className='mb-4'>
        <Typography variant='body3'>
          Company vertical
        </Typography>
        <Select
          value={formData.companyVertical}
          onChange={value => handleDataChange(value, 'companyVertical')}
          required
          fullWidth
          placeholder='Select company vertical'
          items={[
            {
              label: COMPANY_VERTICAL[COMPANY_VERTICAL_ENUM.FIELD_SERVICE],
              value: COMPANY_VERTICAL_ENUM.FIELD_SERVICE
            },
            { label: COMPANY_VERTICAL[COMPANY_VERTICAL_ENUM.GC], value: COMPANY_VERTICAL_ENUM.GC }
          ]}
        />
      </div>
      <IncreaseFreeTrial numberOfDays={numberOfDays} setNumberOfDays={setNumberOfDays} />

      <div>
        <WelcomeEmailFlag
          isSet={formData.send_welcome_email}
          onChange={obj => handleDataChange(obj.send_welcome_email, 'send_welcome_email')}
        />
      </div>

      <div className='flex justify-end'>
        <Button color='primary' size='medium' variant='contained' type='submit' className='text-base'>
          Register
        </Button>
      </div>
    </form>
  );
};

export default RegistrationForm;
